// services
import { ElasticAppSearchAdapter } from "@smeetz/utils-common";

import BookingModel from "../models/BookingModel";
import CustomersModel from "@/app-search/models/CustomersModel";
import { LogService } from "@/services/logging";
import POSConfigModule from "@/store/modules/pos-config";
import { ElasticsearchAccessError } from "@/constants/errors/elasticsearch";

// helpers
import { EngineTypes } from "./request-app-search.service";
import { resolveEndpoint } from "@/config/endpoint-resolver";

/**
 * RequestElasticsearch service
 *
 * Extends ElasticAppSearchAdapter to provide a strongly-typed, domain-specific
 * interface for querying and mapping App Search results to Smeetz data models/collections
 * Handles error reporting and event emission for API latency/errors
 *
 * Usage:
 *   const service = new RequestElasticsearch({ searchKey, engineName });
 *   const { dataModel } = await service.send(payload);
 *
 * @module RequestElasticsearch
 */
export class RequestElasticsearch extends ElasticAppSearchAdapter {
  public indexType: IndexTypes;

  /**
   * Constructs a new RequestElasticsearch service
   *
   * @param opts - Options object containing searchKey and engineName
   *   - searchKey: API key for App Search
   *   - engineName: Logical engine name (see EngineTypes)
   */
  constructor(opts: Record<string, any>) {
    super({
      searchKey: opts.searchKey,
      tableType: MapIndexTypes[opts.engineName],
      serviceUrl: resolveEndpoint("VITE_SERVICES_API"),
    });

    // Map the App Search engine name to the internal index type used for data model mapping and search logic
    this.indexType = MapIndexTypes[opts.engineName];
  }

  /**
   * Sends a search request to App Search and maps the results to the appropriate data model/collection
   *
   * @param {Record<string, any>} payload: Search parameters in App Search format
   * @returns {Promise<any>}
   */
  public async send(payload: Record<string, any>): Promise<any> {
    if (
      POSConfigModule.groupId === 1 ||
      POSConfigModule.groupId === 19017 ||
      import.meta.env.VITE_ENV === "test" ||
      import.meta.env.VITE_ENV === "dev"
    ) {
      // Lets add precision parameter to the payload
      // official doc 1: https://www.elastic.co/guide/en/app-search/current/search-api-precision.html
      // official doc 2: https://www.elastic.co/guide/en/app-search/current/precision-tuning.html
      // this will result in showing orders more precisely if you type in Hamza Dahmoun, because Hamza Kahil will have less score
      // This is still in beta version
      // Thread issue: https://smeetz.slack.com/archives/C02NKSQ1031/p1722975636124539
      // released only for Locarno to not change the behaviour for other groups
      payload.options.precision = 4;
    }

    const startTime = Date.now();

    const response = await this.fetch(payload);

    const endTime = Date.now();
    const latency = endTime - startTime; // Calculate request time
    LogService.info(
      `Appsearch request for ${this.indexType} took ${latency}ms`
    );

    // Map raw App Search results to the correct data model/collection for this index type
    const dataModel = await this.mapResultsToDataModel(
      response.results,
      response.info?.facets || {},
      this.indexType
    );

    return { ...response, dataModel };
  }

  /**
   * Handles errors from API requests
   *
   * @param {Error} error: The error thrown during request
   * @returns {Error}
   */
  protected handleError(error: any): any {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      throw new ElasticsearchAccessError(
        `Elasticsearch returned ${status}: insufficient permissions.`
      );
    }
    return error;
  }

  /**
   * Maps an array of AppSearchResultItem objects to the appropriate Smeetz data model or collection,
   * based on the configured index type. Handles both synchronous and asynchronous mapping logic
   *
   * @param {Array<any>} results: Array of AppSearchResultItem objects from App Search
   * @param {Record<string, any>} facets: Facets object from App Search response (used for PromoCodeCollection)
   * @param {IndexTypes} indexType: The index type (engine) to determine mapping logic
   * @returns {Promise<Array<any>>}
   */
  private async mapResultsToDataModel(
    results: Array<any>,
    facets: Record<string, any>,
    indexType: IndexTypes
  ): Promise<Array<any>> {
    if (indexType === IndexTypes.CustomerList) {
      return results.map((item) => new CustomersModel(item));
    }

    if (indexType === IndexTypes.BookingList) {
      return results.map((item) => new BookingModel(item));
    }

    return [];
  }
}

/**
 * Enum of all supported App Search index types (engines)
 * Values are injected from environment variables for flexibility across deployments
 */
export enum IndexTypes {
  // @ts-ignore
  ProductList = import.meta.env.VITE_ELASTIC_PRODUCTS,
  // @ts-ignore
  TicketList = import.meta.env.VITE_ELASTIC_TICKETS,
  // @ts-ignore
  CustomerList = import.meta.env.VITE_ELASTIC_CUSTOMERS,
  // @ts-ignore
  BookingList = import.meta.env.VITE_ELASTIC_BOOKING,
  // @ts-ignore
  BookingStatementList = import.meta.env.VITE_ELASTIC_BOOKING_STATEMENTS,
  // @ts-ignore
  TimeSlotList = import.meta.env.VITE_ELASTIC_TIME_SLOT,
  // @ts-ignore
  WaiverList = import.meta.env.VITE_ELASTIC_WAIVERS,
}

/**
 * Maps EngineTypes (logical engine names) to IndexTypes (actual index names)
 * Used to resolve the correct index for a given engine
 */
const MapIndexTypes: Record<EngineTypes, IndexTypes> = {
  [EngineTypes.ProductList]: IndexTypes.ProductList,
  [EngineTypes.TicketList]: IndexTypes.TicketList,
  [EngineTypes.CustomerList]: IndexTypes.CustomerList,
  [EngineTypes.BookingList]: IndexTypes.BookingList,
  [EngineTypes.BookingStatementList]: IndexTypes.BookingStatementList,
  [EngineTypes.TimeSlotList]: IndexTypes.TimeSlotList,
  [EngineTypes.WaiverList]: IndexTypes.WaiverList,
};

// Map: which EngineTypes use Elasticsearch
export const EngineTypeElasticEnabled: Record<EngineTypes, boolean> = {
  [EngineTypes.ProductList]: false,
  [EngineTypes.TicketList]: false,
  [EngineTypes.CustomerList]: true,
  [EngineTypes.BookingList]: true,
  [EngineTypes.BookingStatementList]: false,
  [EngineTypes.TimeSlotList]: false,
  [EngineTypes.WaiverList]: true,
};

// List of group IDs for which Elastic engine is enabled
export const ElasticEnabledGroups: number[] = [1, 19066];
